import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Menu as AntMenu,
    Input,
    Divider,
    Card,
    ConfigProvider,
    Typography,
    type ItemType,
    type MenuItemType,
} from '@app/ui';
import {
    SearchOutlined,
    ExclamationCircleOutlined,
    SettingOutlined,
    UserOutlined,
    ApiOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import substringMatch from '@app/utils/substringMatch';
import getUserAndClientsFromStorage from '@app/utils/getUserAndClientsFromStorage';

import {
    RELO_LOGO,
    EDGE_RIGHTSHOLDER_ATTRIBUTION,
    API_LIVE_DOCS,
} from '@app/constants/config';
import userActions from '@app/actions/user';
import connect from '@app/utils/connect';
import useDebounce from '@app/components/hooks/useDebounce';
import {
    updateUserPreferences,
    buildAttributionPatch,
    hasLegacyFlatEntries,
    readEdgeAttributionMode,
} from '@app/utils/updateUserPreferences';

// __BASEPATH__ is a global variable that is replaced at build time
declare const __BASEPATH__: string;
const BASENAME = __BASEPATH__ ? `/${__BASEPATH__}` : '';

const Menu = ({
    name,
    open,
    clients,
    clientId,
    onClose,
    logout,
    headerImgUrl,
}: {
    name: string;
    open: boolean;
    clients: any[];
    clientId: string;
    onClose: () => void;
    logout: () => void;
    headerImgUrl: string;
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const { user } = getUserAndClientsFromStorage();
    const isAdmin = user && user.roles && user.roles.includes('ROLE_ADMIN');
    const navigate = useNavigate();
    const { t } = useTranslation();

    const options: ItemType<MenuItemType>[] = React.useMemo(() => {
        const clientConfigPath = `/c/${clientId}/client-configuration`;
        const accountSettingsPath = `/c/${clientId}/account-settings/password`;
        const apiDocsPath = `/c/${clientId}/api-documentation`;

        const currentClient = clients?.find(
            c => String(c.id) === String(clientId),
        );
        const hasApiLiveDocs =
            !!currentClient?.permissions?.includes(API_LIVE_DOCS);

        return [
            {
                key: 'client-configuration',
                label: (
                    <Typography.Text style={{ fontSize: 14 }}>
                        <SettingOutlined
                            className="-m-r-2"
                            style={{ fontSize: '14px' }}
                        />
                        {t('avatarMenu.clientConfiguration')}
                    </Typography.Text>
                ),
                disabled: !clientId,
                onClick: () => {
                    if (clientId) {
                        navigate(clientConfigPath);
                        onClose();
                    }
                },
            },
            { type: 'divider' as const, key: 'divider-1' },
            {
                key: 'user-settings',
                label: (
                    <Typography.Text style={{ fontSize: 14 }}>
                        <UserOutlined
                            className="-m-r-2"
                            style={{ fontSize: '14px' }}
                        />
                        {t('avatarMenu.userSettings')}
                    </Typography.Text>
                ),
                disabled: !clientId,
                onClick: () => {
                    if (clientId) {
                        navigate(accountSettingsPath);
                        onClose();
                    }
                },
            },
            ...(hasApiLiveDocs
                ? [
                      { type: 'divider' as const, key: 'divider-api' },
                      {
                          key: 'api-documentation',
                          label: (
                              <Typography.Text style={{ fontSize: 14 }}>
                                  <ApiOutlined
                                      className="-m-r-2"
                                      style={{ fontSize: '14px' }}
                                  />
                                  {t('avatarMenu.apiDocumentation')}
                              </Typography.Text>
                          ),
                          disabled: !clientId,
                          onClick: () => {
                              if (clientId) {
                                  navigate(apiDocsPath);
                                  onClose();
                              }
                          },
                      },
                  ]
                : []),
            { type: 'divider' as const, key: 'divider-2' },
            {
                key: 'logout',
                label: (
                    <Typography.Text style={{ fontSize: 14 }}>
                        <i className="fa-regular fa-arrow-right-from-bracket -m-r-2" />
                        {t('avatarMenu.logout')}
                    </Typography.Text>
                ),
                disabled: !clientId,
                onClick: () => {
                    if (clientId) {
                        logout();
                        onClose();
                    }
                },
            },
        ];
    }, [clientId, clients, logout, navigate, onClose, t]);

    const [matchingClients, setMatchingClients] = useState([]);

    // Debounced search term
    const debouncedSearchTerm = useDebounce(searchTerm, 100); // 300ms delay

    // Update matching clients when debounced search term changes
    React.useEffect(() => {
        if (debouncedSearchTerm.length === 0) {
            setMatchingClients([]);
        } else {
            const similar = clients
                .filter(
                    c =>
                        !(c.id === clientId) &&
                        substringMatch(debouncedSearchTerm, c.name),
                )
                .sort((a, b) => a.name.localeCompare(b.name));

            setMatchingClients(similar);
        }
    }, [debouncedSearchTerm, clients, clientId]);

    const updateSearchTerm = e => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    const handleClientClick = async (selectedClientId: number) => {
        // Check if the selected client has the EDGE_RIGHTSHOLDER_ATTRIBUTION permission
        const selectedClient = clients.find(c => c.id === selectedClientId);
        const hasEdgeRightsholderAttribution =
            selectedClient?.permissions?.includes(
                EDGE_RIGHTSHOLDER_ATTRIBUTION,
            );

        // Read preferences from localStorage (already populated by login/selectClient)
        const userPreferences = user?.userPreferences || {};
        const rightsholderAttribution =
            userPreferences.rightsholder_attribution || {};
        // Counts a mode stored under a legacy flat key as existing, so opening
        // a client never resets the mode it was left in.
        const existingAttribution = readEdgeAttributionMode(
            rightsholderAttribution,
            selectedClientId,
        );
        const needsMigration = hasLegacyFlatEntries(rightsholderAttribution);

        if (
            hasEdgeRightsholderAttribution &&
            (!existingAttribution || needsMigration)
        ) {
            // Seed 'team' for a client with no mode, keep the existing one
            // otherwise, and migrate any legacy flat entries either way.
            await updateUserPreferences({
                userId: user.id,
                preferences: {
                    rightsholder_attribution: buildAttributionPatch(
                        rightsholderAttribution,
                        selectedClientId,
                        existingAttribution || 'team',
                    ),
                },
            });
        }
    };

    return (
        <ConfigProvider
            theme={{
                components: {
                    Card: {
                        actionsBg: '#D6DEE1',
                        headerBg: '#D6DEE1',
                        bodyPadding: 0,
                    },
                    Menu: {
                        itemBg: '#D6DEE1',
                        colorBorder: '#b7b6b6',
                        borderRadius: 4,
                    },
                    Input: {
                        hoverBorderColor: '#009FDF',
                    },
                },
            }}
        >
            <Card
                style={{
                    width: 320,
                    zIndex: 10000,
                    display: open ? 'block' : 'none',
                    position: 'absolute',
                    right: 0,
                    top: 40,
                    backgroundColor: '#D6DEE1',
                    overflow: 'hidden',
                    border: '1px solid #bfbfb',
                    boxShadow: 'rgba(0, 0, 0, 0.1) 4px 4px 8px 2px',
                }}
                title={
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '12px',
                        }}
                        className="-ellipsis"
                    >
                        <img
                            src={headerImgUrl || RELO_LOGO}
                            style={{
                                width: '48px',
                            }}
                        />
                        <Typography.Text
                            className="-ellipsis"
                            style={{
                                margin: 0,
                                fontSize: 16,
                                fontWeight: 500,
                            }}
                        >
                            {name}
                        </Typography.Text>
                    </div>
                }
            >
                {(isAdmin || clients.length > 4) && (
                    <div
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#F1F2F3',
                            width: '100%',
                        }}
                    >
                        <Input
                            placeholder={t('avatarMenu.searchClient')}
                            value={searchTerm}
                            onChange={updateSearchTerm}
                            prefix={
                                <SearchOutlined style={{ color: '#BFBFBF' }} />
                            }
                            suffix={
                                <>
                                    {debouncedSearchTerm && (
                                        <i className="fa-regular fa-chevron-down" />
                                    )}
                                </>
                            }
                        />
                    </div>
                )}

                <Divider
                    style={{
                        margin: 0,
                    }}
                />

                <ConfigProvider
                    theme={{
                        components: {
                            Menu: {
                                itemBg: '#F1F2F3',
                            },
                        },
                    }}
                >
                    <AntMenu
                        mode="vertical"
                        selectable={false}
                        style={{
                            border: 'none',
                            maxHeight: 320,
                            minHeight: Math.min(
                                320,
                                matchingClients.length * 60,
                            ),
                            overflow: 'auto',
                        }}
                        theme="light"
                        items={matchingClients.map(c => ({
                            key: `client-${c.id}`,
                            title: c.name,
                            label: (
                                <Link
                                    to={`${BASENAME}/c/${c.id}`}
                                    target="_blank"
                                    style={{ width: '100%' }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 40,
                                                overflow: 'hidden',
                                                marginRight: 16,
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <img
                                                src={
                                                    c.headerImageUrl ||
                                                    RELO_LOGO
                                                }
                                                alt={c.name}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <span
                                            style={{ fontSize: 14 }}
                                            className="-ellipsis"
                                        >
                                            {c.name}
                                        </span>
                                    </div>
                                </Link>
                            ),
                            style: {
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                            },
                            onClick: () => {
                                handleClientClick(c.id);
                                onClose();
                            },
                        }))}
                    />
                </ConfigProvider>
                {!matchingClients.length &&
                    searchTerm &&
                    clients.length > 4 && (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '12px 0',
                            }}
                        >
                            <ExclamationCircleOutlined
                                style={{ marginRight: 8 }}
                            />
                            {t('avatarMenu.noMatchingClients')}
                        </div>
                    )}

                <Divider
                    style={{
                        margin: 0,
                    }}
                />
                <AntMenu
                    mode="vertical"
                    selectable={false}
                    style={{ border: 'none' }}
                    theme="light"
                    items={options}
                />
            </Card>
        </ConfigProvider>
    );
};

export default connect([], userActions)(Menu);
